import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { OptionType } from 'src/helper/shared-types';
import { CLAIM_REQUEST_SCREEN_KEYS } from 'src/views/VendorPortal/Request/SidePanel/ClaimPanel/constants';
import { PASS_REQUEST_SCREEN_KEYS } from 'src/views/VendorPortal/Request/SidePanel/PassPanel/constants';
import { getCategoryOptions } from '../work-order/api';
import type { WorkOrderIssueType } from '../work-order/types';
import {
  addRequestAvailabilityFromWorkOrder,
  claimRequest,
  getRequestDetails,
  getRequests,
  getVendorAvailability,
  passRequest,
} from './api';
import type {
  RequestAPIResponsePayload,
  RequestDataType,
  RequestListingType,
  RequestStateType,
} from './types';

const extraReducers = (builder: ActionReducerMapBuilder<RequestStateType>) => {
  builder.addCase(getRequests().pending, (state: RequestStateType, action) => {
    state.isLoading = true;
    state.currentFilterReferenceObj = action.meta.arg;
  });

  builder.addCase(
    getRequests().fulfilled,
    (state: RequestStateType, action: PayloadAction<RequestAPIResponsePayload>) => {
      const formattedData: RequestListingType[] = action.payload.data.map(
        (data: RequestDataType) => ({
          work_order_id: data.work_order_id,
          address: data.property.address,
          category: {
            label: data.work_order_issues[0].problem.category.label,
            priority: data.priority,
          },
          issueCount: data.work_order_issues.length,
          isViewed: data.allocation.is_viewed,
          nte: (Number(data.nte_amount_in_cents) || 0) / 100,
          received: {
            data: data.vendor_allocated_at,
            timezone: data.timezone,
          },
          hasRequestedAvailability: Boolean(
            data.availability_request && data.availability_request.length > 0,
          ),
        }),
      );

      if (action.payload.meta.current_page > 1) {
        // ORI-572: infinite-scroll pages can legitimately re-return a row. A realtime
        // `vendor.workOrder.dispatch` frame inserts a new row at the top of the default
        // `-received` DESC sort, which shifts every server-side page boundary by one, so the
        // next page re-sends a row already held in state. Dedupe by work_order_id, keep-first
        // (the held row preserves local mutations such as hasRequestedAvailability).
        // Rows with a falsy/absent work_order_id are ALWAYS kept: a bare `seen.has(...)`
        // predicate would silently delete them (ORI-571 regression class).
        const seen = new Set(state.data.map((r) => r.work_order_id));
        state.data = [
          ...state.data,
          ...formattedData.filter((r) => {
            if (!r.work_order_id) return true;
            if (seen.has(r.work_order_id)) return false;
            // Grow the set while filtering so a page that repeats an id WITHIN itself is
            // deduped too, not just incoming-vs-held.
            seen.add(r.work_order_id);
            return true;
          }),
        ];
      } else {
        state.data = formattedData;
        state.isFilterLoading = false;
      }

      state.isFilteringList = false;
      state.isLoading = false;
      state.meta = action.payload.meta;
    },
  );

  builder.addCase(getRequests().rejected, (state: RequestStateType, action) => {
    // ORI-572: a canceled fetch is not a failure — do not blank the list. The realtime
    // dispatch handler calls getData(), whose first act is abortControllerRef.current?.abort()
    // (useRequestPage.tsx:36); that abort rejects with `{ actionType: 'canceled' }`
    // (src/helper/api.ts:148) and the superseding request is already in flight. Without this
    // early return the canceled rejection clears state.data and the vendor sees an empty
    // Requests list until the replacement fetch resolves. Dormant before ORI-572 only because
    // no dispatch frame ever arrived for a select-all broadcast.
    //
    // Returning BEFORE the flag resets is deliberate: the superseding request owns them, and
    // clearing isLoading in the gap would flash the loaded list before the next `pending` puts
    // the skeleton back. It cannot strand the flags, because an abort is always followed by a
    // replacement dispatch — `getAccessToken()` (src/helper/utils.ts:68-98) catches internally
    // and resolves to '' rather than rejecting, so getData() cannot bail between the abort on
    // line 36 and the appDispatch on line 48. On unmount the cleanup aborts and then dispatches
    // resetRequestListState() (useRequestPage.tsx:144-149), which empties data/meta/filters and
    // clears isFilteringList — it does NOT touch isLoading or isFilterLoading, so those two are
    // left as the next mount's `pending` finds them (`pending` sets isLoading itself).
    if ((action.payload as { actionType?: string })?.actionType === 'canceled') return;

    state.isFilteringList = false;
    state.isFilterLoading = false;
    state.isLoading = false;
    state.data = [];
  });

  // Fetch Request details
  builder.addCase(getRequestDetails().pending, (state: RequestStateType) => {
    state.sidePanels.requestDetails.isLoading = true;
    // ORI-627: a new attempt (initial load or Retry) supersedes the previous failure.
    state.sidePanels.requestDetails.hasDetailsError = false;
  });
  builder.addCase(getRequestDetails().fulfilled, (state: RequestStateType, action) => {
    const payload: RequestDataType = action?.payload;
    const formattedData: RequestDataType = {
      ...payload,
      work_order_issues: payload?.work_order_issues?.map((issue: WorkOrderIssueType) => ({
        ...issue,
        ...(issue.issue_recommended_items && {
          recommended_tools: issue.issue_recommended_items.filter(
            (item) => item.item_type === 'tool',
          ),
          recommended_materials: issue.issue_recommended_items.filter(
            (item) => item.item_type === 'material',
          ),
        }),
      })),
    };
    state.source = formattedData;
    state.sidePanels.requestDetails = {
      ...state.sidePanels.requestDetails,
      isLoading: false,
      needToRefetch: false,
      hasDetailsError: false,
    };
  });
  builder.addCase(getRequestDetails().rejected, (state: RequestStateType, action) => {
    // ORI-627: a canceled rejection belongs to a superseded request — the mount effect
    // aborts on every id change. Without this early return, aborting request A's
    // in-flight GET clears request B's loading state and blanks its error flag.
    // Mirrors the work-order slice's rejected reducer, which has always had this guard.
    const actionType = (action.payload as { actionType?: string } | undefined)?.actionType;
    if (actionType === 'canceled') return;

    state.sidePanels.requestDetails = {
      ...state.sidePanels.requestDetails,
      isLoading: false,
      needToRefetch: false,
      // The 401 sign-out shape is not a load failure — the api helper is already
      // redirecting to /login, so an error state would render over the redirect.
      hasDetailsError: actionType !== 'unauthorized',
    };
  });

  builder.addCase(
    getCategoryOptions().fulfilled,
    (state: RequestStateType, action: PayloadAction<OptionType[]>) => {
      state.categoryOptions = action.payload;
      state.isFilterLoading = false;
    },
  );

  builder.addCase(getCategoryOptions().rejected, (state: RequestStateType) => {
    state.isFilterLoading = false;
  });

  // Vendor availability
  builder.addCase(getVendorAvailability().pending, (state: RequestStateType) => {
    state.isFetchingVendorAvailability = true;
  });
  builder.addCase(getVendorAvailability().fulfilled, (state: RequestStateType) => {
    state.isFetchingVendorAvailability = false;
  });
  builder.addCase(getVendorAvailability().rejected, (state: RequestStateType) => {
    state.isFetchingVendorAvailability = false;
  });

  // Claim request
  // ORI-420 (AC-2): evict the claimed row from the available pool the instant the claim
  // succeeds, so it never lingers regardless of how the success panel is later dismissed.
  // `action.meta.arg` is the claim body (not the id), and requestDetails.workOrderUuid is a
  // separately-owned field that can change mid-flight (popstate URL sync / panel close), so
  // snapshot the submitting id at `pending` and evict from that snapshot at `fulfilled`.
  builder.addCase(claimRequest().pending, (state: RequestStateType) => {
    state.sidePanels.claimRequest.claimingWorkOrderUuid =
      state.sidePanels.requestDetails.workOrderUuid;
  });
  builder.addCase(claimRequest().fulfilled, (state: RequestStateType) => {
    state.sidePanels.claimRequest.currentScreen = CLAIM_REQUEST_SCREEN_KEYS.CLAIM_SUCCESS;
    const claimedId = state.sidePanels.claimRequest.claimingWorkOrderUuid;
    if (claimedId) {
      state.data = state.data.filter((item) => item.work_order_id !== claimedId);
    }
    state.sidePanels.claimRequest.claimingWorkOrderUuid = undefined;
  });
  builder.addCase(claimRequest().rejected, (state: RequestStateType) => {
    state.sidePanels.claimRequest.claimingWorkOrderUuid = undefined;
  });

  // Pass request
  // The success text says the request was removed, so drop the row at `fulfilled` too, from
  // the id snapshotted at `pending` (same reasoning as the claim above).
  builder.addCase(passRequest().pending, (state: RequestStateType) => {
    state.sidePanels.passRequest.passingWorkOrderUuid =
      state.sidePanels.requestDetails.workOrderUuid;
  });
  builder.addCase(passRequest().fulfilled, (state: RequestStateType) => {
    state.sidePanels.passRequest.currentScreen = PASS_REQUEST_SCREEN_KEYS.PASS_SUCCESS;
    const passedId = state.sidePanels.passRequest.passingWorkOrderUuid;
    if (passedId) {
      state.data = state.data.filter((item) => item.work_order_id !== passedId);
    }
    state.sidePanels.passRequest.passingWorkOrderUuid = undefined;
  });
  builder.addCase(passRequest().rejected, (state: RequestStateType) => {
    state.sidePanels.passRequest.passingWorkOrderUuid = undefined;
  });

  // Add request availability
  builder.addCase(addRequestAvailabilityFromWorkOrder().fulfilled, (state: RequestStateType) => {
    const workOrderId = state.sidePanels.requestAvailability.data?.work_order_id;

    if (workOrderId) {
      state.data = state.data.map((request) => {
        if (request.work_order_id === workOrderId) {
          return {
            ...request,
            hasRequestedAvailability: true,
          };
        }
        return request;
      });
    }
  });
};

export default extraReducers;
